import React, { useState } from "react"
import { motion } from "framer-motion"
import {Search, Edit2, Trash2, Phone, Printer, Users, Eye} from 'lucide-react'
import { useGuias, Feirante } from "../context/GuiasContext"

interface ListarFeirantesProps {
  onEdit?: (feiranteId: string) => void
}

export default function ListarFeirantes({ onEdit }: ListarFeirantesProps) {
  const { feirantes, deleteFeirante } = useGuias()
  const [searchTerm, setSearchTerm] = useState("")

  const feirantesFiltrados = feirantes.filter((feirante) =>
    feirante.nomeCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    feirante.cpf.includes(searchTerm) ||
    feirante.contato.includes(searchTerm)
  )

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este feirante?")) {
      await deleteFeirante(id)
    }
  }

  const abrirWhatsApp = (contato: string) => {
    const numero = contato.replace(/\D/g, "")
    window.open(`https://wa.me/55${numero}`, "_blank")
  }

  const handlePrint = () => {
    window.print()
  }

  const handlePrintIndividual = (feirante: Feirante) => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ficha do Feirante - ${feirante.nomeCompleto}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 40px; 
              background: white;
              color: #000;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 3px solid #004161;
              padding-bottom: 20px;
            }
            .header h1 {
              color: #004161;
              font-size: 28px;
              margin-bottom: 10px;
            }
            .lote-badge {
              display: inline-block;
              background: #004161;
              color: white;
              padding: 15px 30px;
              border-radius: 12px;
              font-size: 24px;
              font-weight: bold;
              margin: 20px 0;
            }
            .info-section {
              margin: 30px 0;
            }
            .info-row {
              display: flex;
              padding: 12px;
              border-bottom: 1px solid #e0e0e0;
            }
            .info-row:nth-child(even) {
              background: #f9f9f9;
            }
            .info-label {
              font-weight: bold;
              width: 200px;
              color: #004161;
            }
            .info-value {
              flex: 1;
              color: #333;
            }
            .footer {
              margin-top: 60px;
              padding-top: 20px;
              border-top: 2px solid #004161;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FICHA DE CADASTRO DE FEIRANTE</h1>
            <p>Data de Emissão: ${new Date().toLocaleDateString('pt-BR')}</p>
            ${feirante.numeroLote ? `<div class="lote-badge">LOTE Nº ${feirante.numeroLote}</div>` : ''}
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <div class="info-label">Nome Completo:</div>
              <div class="info-value">${feirante.nomeCompleto}</div>
            </div>
            <div class="info-row">
              <div class="info-label">CPF:</div>
              <div class="info-value">${feirante.cpf}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Contato (WhatsApp):</div>
              <div class="info-value">${feirante.contato}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Endereço:</div>
              <div class="info-value">${feirante.endereco}</div>
            </div>
            <div class="info-row">
              <div class="info-label">UF (Estado):</div>
              <div class="info-value">${feirante.uf}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Endereço do Lote:</div>
              <div class="info-value">${feirante.enderecoLote}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Número do Lote:</div>
              <div class="info-value">${feirante.numeroLote}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Valor Pago Semanalmente:</div>
              <div class="info-value">R$ ${feirante.valorSemanal}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Categorias:</div>
              <div class="info-value">${(feirante.categorias || []).join(', ') || 'Não informado'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Data de Cadastro:</div>
              <div class="info-value">${new Date(feirante.createdAt).toLocaleDateString('pt-BR')}</div>
            </div>
          </div>

          <div class="footer">
            <p>Sistema EMIT - Gestão Municipal</p>
            <p>Documento gerado automaticamente em ${new Date().toLocaleString('pt-BR')}</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  const calcularTotalSemanal = () => {
    return feirantes.reduce((total, feirante) => {
      const valor = parseFloat(feirante.valorSemanal.replace(",", "."))
      return total + (isNaN(valor) ? 0 : valor)
    }, 0)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Cabeçalho */}
      <div className="bg-white rounded-lg shadow-lg p-6 no-print">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#004161] rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Feirantes Cadastrados</h2>
              <p className="text-sm text-gray-500">
                Total: {feirantes.length} feirante(s) | Arrecadação Semanal: R$ {calcularTotalSemanal().toFixed(2).replace(".", ",")}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrint}
            className="flex items-center gap-2 bg-[#004161] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow hover:bg-[#00567d]"
          >
            <Printer className="w-5 h-5" />
            Imprimir Lista
          </motion.button>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou contato..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabela para Impressão */}
      <div className="print-only">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Lista de Feirantes Cadastrados</h1>
          <p className="text-gray-600">Data: {new Date().toLocaleDateString("pt-BR")}</p>
          <p className="text-gray-600 font-semibold mt-2">
            Total de Feirantes: {feirantes.length} | Arrecadação Semanal: R$ {calcularTotalSemanal().toFixed(2).replace(".", ",")}
          </p>
        </div>
        
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 border-b-2 border-gray-300">
              <th className="text-left p-3 border border-gray-300 font-semibold">Nome Completo</th>
              <th className="text-left p-3 border border-gray-300 font-semibold">CPF</th>
              <th className="text-left p-3 border border-gray-300 font-semibold">Endereço</th>
              <th className="text-center p-3 border border-gray-300 font-semibold">UF</th>
              <th className="text-left p-3 border border-gray-300 font-semibold">Endereço do Lote</th>
              <th className="text-center p-3 border border-gray-300 font-semibold">Nº Lote</th>
              <th className="text-left p-3 border border-gray-300 font-semibold">Categorias</th>
              <th className="text-center p-3 border border-gray-300 font-semibold">Contato</th>
              <th className="text-right p-3 border border-gray-300 font-semibold">Valor Semanal</th>
            </tr>
          </thead>
          <tbody>
            {feirantes.map((feirante, index) => (
              <tr key={feirante._id} className={index % 2 === 0 ? "bg-white" : "bg-gray-30"}>
                <td className="p-3 border border-gray-300">{feirante.nomeCompleto}</td>
                <td className="p-3 border border-gray-300">{feirante.cpf}</td>
                <td className="p-3 border border-gray-300">{feirante.endereco}</td>
                <td className="p-3 border border-gray-300 text-center">{feirante.uf}</td>
                <td className="p-3 border border-gray-300">{feirante.enderecoLote}</td>
                <td className="p-3 border border-gray-300 text-center font-semibold">{feirante.numeroLote}</td>
                <td className="p-3 border border-gray-300">{(feirante.categorias || []).join(', ') || 'Não informado'}</td>
                <td className="p-3 border border-gray-300 text-center">{feirante.contato}</td>
                <td className="p-3 border border-gray-300 text-right">R$ {feirante.valorSemanal}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-200 font-bold">
              <td colSpan={8} className="p-3 border border-gray-300 text-right">TOTAL SEMANAL:</td>
              <td className="p-3 border border-gray-300 text-right">R$ {calcularTotalSemanal().toFixed(2).replace(".", ",")}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Tabela para Tela */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden no-print">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#004161] text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">Nome Completo</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">CPF</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Endereço</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">UF</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Endereço do Lote</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Nº Lote</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Categorias</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Contato</th>
                <th className="px-6 py-4 text-right text-sm font-semibold">Valor Semanal</th>
                <th className="px-6 py-4 text-center text-sm font-semibold" style={{minWidth: '220px'}}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {feirantesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    Nenhum feirante encontrado
                  </td>
                </tr>
              ) : (
                feirantesFiltrados.map((feirante) => (
                  <tr key={feirante._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {feirante.nomeCompleto}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{feirante.cpf}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{feirante.endereco}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                        {feirante.uf}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{feirante.enderecoLote}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="inline-block px-3 py-1 bg-[#004161] text-white rounded-lg font-bold">
                        {feirante.numeroLote}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex flex-wrap gap-1">
                        {(feirante.categorias || []).map((cat, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs font-medium">
                            {cat}
                          </span>
                        ))}
                        {(!feirante.categorias || feirante.categorias.length === 0) && (
                          <span className="text-gray-400 text-xs">Não informado</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <button
                        onClick={() => abrirWhatsApp(feirante.contato)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        title="Abrir WhatsApp"
                      >
                        <Phone className="w-4 h-4" />
                        {feirante.contato}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold text-right">
                      R$ {feirante.valorSemanal}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handlePrintIndividual(feirante)}
                          className="p-2 bg-[#004161] text-white rounded-lg hover:bg-[#00567d] transition-colors"
                          title="Visualizar e Imprimir"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onEdit?.(feirante._id!)}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(feirante._id!)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  )
}
